import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteLoyaltyPoints,
  getLoyaltyPointsListList,
  handleClear,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
} from "store/liveOps/loyalty/points/slice";
import { loyaltyListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import PointsFilter from "../PointsFilter";
import {
  NotificationTypes,
  showNotification,
} from "../../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import BooleanLabel from "components/common/BooleanLabel/BooleanLabel";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import styles from "./DepositPointsList.module.scss";

const DepositPointsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;

  const [order, setOrder] = useState<{
    points: string;
    currency: string;
    id: string;
    numberOfDeposit: string;
  }>({
    points: "none",
    currency: "none",
    id: "none",
    numberOfDeposit: "none",
  });
  const [currentLimit, setLimit] = useState<number>(loyaltyListLimits[0]);
  const [dep, setDep] = useState<string>("");
  const [currencies, setCurrencies] = useState<
    { value: number | string; label: string }[]
  >([]);

  const list = useAppSelector((store) => store["loyalty-points"].list);
  const isListFetched = useAppSelector(
    (store) => store["loyalty-points"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["loyalty-points"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["loyalty-points"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["loyalty-points"].activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store["loyalty-points"].selectedItems,
  );

  const processingItemId = useAppSelector(
    (store) => store["loyalty-points"].processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'loyalty-points',
    isListFetched
  });

  useEffect(() => {
    return () => {
      dispatch(handleClear());
    };
  }, []);
  useEffect(() => {
    if (canRead && !isListFetched) {
      dispatch(
        getLoyaltyPointsListList({
          limit: currentLimit,
          offset: activePage - 1,
          type: "deposit",
          currencies: currencies.map((el) => el.value as string),
          numberOfDeposit: dep || undefined,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order, dep, canRead]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOpenEditModal = (id: number) => {
    if (!canRead) return;
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.loyaltyPoints}&entityId=${id}&type=deposit`,
    );
    dispatch(openModal({ activeModal: ModalTypes.loyaltyPoints }));
  };
  const handleOpenCreateModal = () => {
    if (!canCreate) return;
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.loyaltyPoints}&type=deposit`,
    );
    dispatch(openModal({ activeModal: ModalTypes.loyaltyPoints }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;
    const data = { id: id ?? 0, type: "deposit" };
    const result = await dispatch(deleteLoyaltyPoints(data));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
      dispatch(handlePaginate(activePage));
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  if (!canRead) return null;

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PointsFilter
        type="deposit"
        selectedCurrency={currencies}
        handleUpdateCurrency={setCurrencies}
        depValue={dep}
        handleDep={setDep}
        handleApply={() => dispatch(handlePaginate(1))}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn
                txt={t("addPoints")}
                handleClick={handleOpenCreateModal}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={7} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noDepositPointsFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={list}
                  className={styles.depositPointsTable}
                  columns={{
                    hiddenColumns: ["loyaltyLevelId"],
                    sortingColumns: {
                      currency: {
                        hasSorting: true,
                        value: t("currency"),
                      },
                      numberOfDeposit: {
                        hasSorting: true,
                        value: t("numberOfDeposit"),
                      },
                    },
                  }}
                  order={order}
                  idField="id"
                  selectedItems={selectedItems}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onDelete={(id) => handleDeleteItem(id)}
                  onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                  onView={
                    canRead && !canUpdate
                      ? (id) => handleOpenEditModal(id)
                      : undefined
                  }
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  hasCheckbox={false}
                  hasEdit={canUpdate}
                  hasView={canRead && !canUpdate}
                  hasDelete={canDelete}
                  hasOptions={canRead || canUpdate || canDelete}
                  processingId={processingItemId}
                  customColumns={(row) => ({
                    useDepositAmount: (
                      <BooleanLabel value={row.useDepositAmount} />
                    ),
                  })}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={loyaltyListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DepositPointsList;
